import type { TextWithSidePanelComponent } from "../../types";
import { RuntimeImage, RuntimeLink } from "../../runtime/RendererRuntime";
import { isParagraphHtml, sanitizeParagraphHtml } from "../../utils/paragraphHtml";
import { resolveLinkHref } from "../../utils/links";
import { getImageObjectPositionStyle, getImageOverrideProps, mergeClassNames, mergeStyles, resolveImageValue } from "../../utils/imageOptions";

/** Text with side panel renderer containing image and CTA button. */
export function TextWithSidePanelRenderer({ component }: { component: TextWithSidePanelComponent }) {
  const { title, paragraphs, panelTitle, imageSrc, imageAlt, ctaLabel, ctaHref } = component.content;
  const { src, options } = resolveImageValue(imageSrc);
  const objectPositionStyle = getImageObjectPositionStyle(options);
  const overrideProps = getImageOverrideProps(options);
  const resolvedCtaHref = resolveLinkHref(ctaHref);

  return (
    <section>
      <div className="grid gap-4 md:grid-cols-[2fr_1fr] md:items-start">
        <div className="rounded-sm bg-[#F3F4F4] p-4 md:p-6">
          <h3 className="text-lg xl:text-xl font-semibold text-[#00B862] md:text-xl">{title}</h3>
          <div className="mt-4 space-y-4 text-base xl:text-lg leading-7 text-[#121212] rich-paragraph-block">
            {(paragraphs ?? []).map((paragraph, index) =>
              isParagraphHtml(paragraph) ? (
                <p
                  key={index}
                  className="rich-paragraph-block"
                  dangerouslySetInnerHTML={{ __html: sanitizeParagraphHtml(paragraph) }}
                />
              ) : (
                <p key={index}>{paragraph}</p>
              )
            )}
          </div>
        </div>
        <aside className="flex flex-col gap-4 bg-transparent p-4 pt-0 md:p-5 md:pt-0">
          <h4 className="text-lg xl:text-xl font-semibold text-[#00B862] md:text-xl">{panelTitle}</h4>
          <RuntimeImage
            src={src}
            alt={imageAlt}
            className={mergeClassNames("w-full object-cover aspect-[4/3]", overrideProps.className)}
            style={mergeStyles(objectPositionStyle, overrideProps.style)}
            priority
          />
          {resolvedCtaHref && ctaLabel ? (
            <RuntimeLink
              href={resolvedCtaHref}
              className="inline-flex self-start items-center justify-center rounded-full bg-[#FFC105] px-4 py-2 text-sm xl:text-base font-semibold text-white transition hover:opacity-90"
            >
              {ctaLabel}
            </RuntimeLink>
          ) : null}
        </aside>
      </div>
    </section>
  );
}
